<template>
  <article class="accounts-dashboard">
    <div class="container">
      <header>
        <router-link :to="`/app/clients/${$route.params.id}`" class="back">
          <svgicon name="chevron-down" class="chevron" alt="Back" />
          <span class="link-label">{{ firstName || 'Back' }}</span>
        </router-link>
        <h4 v-t class="title">Overview</h4>
      </header>
      <account-graph
        v-if="loaded"
        class="graph"
        :timeframe="timeframe"
        :balance="balance"
        :cashflows="cashflows"
        :ror="ror"
        :gains="gains"
        :dates="dates"
        :balances="balances"
        :contributions="contributions"
        @time-frame-changed="changeTimeFrame"
      />
      <div class="accounts">
        <div class="header">
          <span>Account</span>
          <span v-t class="balance-header">Balance</span>
          <span v-t class="small-screen-hide return-header">Return</span>
          <span v-t class="small-screen-hide">Portfolio</span>
          <span />
        </div>
        <router-link v-for="account in accountList" :key="account.id" :to="`/app/clients/${$route.params.id}/${account.id}`" class="account">
          <span class="h5">{{ account.name ? account.name.substring(0, 20) : account.accountTypeName }}</span>
          <span><span class="balance-value">{{ account.balance | formatCurrencyRounded }}</span></span>
          <span class="small-screen-hide">
            <span class="return-value">{{ account.balance === '0.0' ? null : account.ror | formatPercentage }}</span>
          </span>
          <span class="small-screen-hide">{{ account.portfolioModelName || '' }}</span>
          <span>
            <svgicon name="chevron-down" height="1.3rem" width="1.3rem" class="chevron" :alt="$t('Account')" />
          </span>
        </router-link>
      </div>
    </div>
  </article>
</template>

<script>
import moment from 'moment';
import filters, { formatDateShortMonth } from '@/lib/utils/filters';
import getBrandConfig from '@/lib/utils/get_brand_config';
import { accountGraph } from '@/components/advisor';
import getInvestmentAccounts from '@/store/data-providers/get_investment_accounts';
import '@/lib/icons/chevron-down';
import errorStore from '@/store/error';

const _ = {
  errorStore,
  getInvestmentAccounts,
  formatDateShortMonth,
  brandConfig: getBrandConfig,
};

export default {
  _,

  route: {
    path: '/app/clients/:id/overview',
  },
  head() {
    return {
      title: `${_.brandConfig.fullName}`,
    };
  },
  components: {
    accountGraph,
  },
  filters,

  data() {
    return {
      loaded: false,
      firstName: null,
      timeframe: 'all',
      balance: null,
      cashflows: null,
      ror: null,
      gains: null,
      dates: null,
      balances: null,
      contributions: null,
      accountList: null,
    };
  },

  async created() {
    await this.updateData();
  },
  methods: {
    timeframeToDate() {
      let startDate = null;

      if (this.timeframe === '1M') {
        startDate = moment().add(-1, 'month');
      } else if (this.timeframe === '6M') {
        startDate = moment().add(-6, 'month');
      } else if (this.timeframe === '1Y') {
        startDate = moment().add(-1, 'year');
      } else if (this.timeframe === 'YTD') {
        startDate = moment().set({ date: 1, month: 0 });
      }

      return startDate ? startDate.format('YYYY-MM-DD') : null;
    },
    async updateData() {
      const startDate = this.timeframeToDate();
      try {
        const {
          userDetails,
          allAccountsSumUp,
          dataPoints,
          accountList,
        } = await _.getInvestmentAccounts(this.$route.params.id, startDate);

        this.setAccountSumUp(allAccountsSumUp);
        this.setGraphDataPoints(dataPoints);
        this.firstName = userDetails.firstName;
        this.accountList = accountList;

        // We need a loader to display something while it is loading
        this.loaded = true;
      } catch (exception) {
        _.errorStore.addError();
      }
    },
    changeTimeFrame(newTimeframe) {
      this.timeframe = newTimeframe;
      this.updateData();
    },
    setAccountSumUp(allAccountsSumUp) {
      this.balance = parseFloat(allAccountsSumUp.endBalance);
      this.cashflows = allAccountsSumUp.cashFlows;
      this.ror = allAccountsSumUp.ror;
      this.gains = allAccountsSumUp.return;
    },
    setGraphDataPoints(dataPoints) {
      this.dates = dataPoints.dates.map(date => _.formatDateShortMonth(date));
      this.balances = dataPoints.balances;
      this.contributions = dataPoints.contributions;
    },
  },
};
</script>

<style lang="scss" scoped>


  .accounts-dashboard {
    padding: 0 0.75rem;
    background-color: $pearl-50;
    overflow-y: auto;

    .container {
      max-width: 80rem;
      width: 100%;
      margin: 0 auto;
    }
  }

  header {
    height: 3.5rem;
    line-height: 3.5rem;
    text-align: center;
    border-bottom: 1px solid $pearl-300;

    .back {
      position: absolute;
      height: 100%;
      left: 0;
      display: flex;
      align-items: center;
      text-decoration: none;

      .link-label {
        display: none;

        @media #{$screen-width-medium} {
          display: inline;
        }
      }
    }

    .chevron {
      transform: rotate(90deg);
      margin-right: 0.3rem;
    }

    .title {
      display: inline-block;
      line-height: 1;
      text-align: center;
      margin: 0;
    }
  }

  .graph {
    margin-top: 2.5rem;
  }

  .accounts {
    display: table;
    width: 100%;
    margin-top: 1rem;
    border-spacing: 0 0.5rem;

    .header, .account {
      display: table-row;
      text-decoration: none;
      color: $pearl-900;

      > span {
        display: table-cell;

        &:first-child {
          padding-left: 1rem;
        }

        &:last-child {
          width: 2rem;
          padding-right: 1rem;

          .chevron {
            bottom: 2px;
            transform: rotate(-90deg);
            color: $primary-300;
          }
        }
      }
    }

    .header {
      color: $pearl-700;

      .balance-header {
        padding-left: 3.5rem;
      }

      .return-header {
        padding-left: 2rem;
      }
    }

    .account {
      border-radius: 0.5rem;

      > span {
        background-color: $white;
        padding-top: 0.75rem;
        padding-bottom: 0.75rem;
        border-top: 1px solid transparent;
        border-bottom: 1px solid $pearl-300;

        &:first-child {
          border-radius: 0.25rem 0 0 0.25rem;
          border-left: 1px solid transparent;
        }

        &:last-child {
          border-radius: 0 0.25rem 0.25rem 0;
          border-right: 1px solid transparent;
        }
      }

      &:hover {
        > span {
          background-color: $pearl-50;
          border-top: 1px solid $pearl-100;
          border-bottom: 1px solid $pearl-100;

          &:first-child {
            border-left: 1px solid $pearl-100;
          }

          &:last-child {
            border-right: 1px solid $pearl-100;
          }
        }
      }

      .balance-value {
        display: inline-block;
        width: 7rem;
        text-align: right;
      }

      .return-value {
        display: inline-block;
        width: 5rem;
        text-align: right;
      }
    }
  }

</style>
